import FilterAltIcon from '@mui/icons-material/FilterAlt'
import {Badge, Box, Button, IconButton, Popover, Tooltip, Typography} from '@mui/material'
import type {FC, MouseEvent as ReactMouseEvent} from 'react'
import {useState} from 'react'
import type {AppbarHierarchyFilterState} from './useAppbarHierarchyFilter.ts'
import BasicHierarchyFiltersRow from '@shared/components/base/filters/BasicHierarchyFiltersRow.tsx'
import {useCustomTranslation} from '@shared/hooks/useCustomTranslation.ts'

type Obj = {id: string; name: string}

interface Props {
  filterState: AppbarHierarchyFilterState
  activeCount: number
  productionAreas: Obj[]
  lines: Obj[]
  equipments: Obj[]
  searchHandlers: {
    handleAreaSearch: (search: string) => void
    handleLineSearch: (search: string) => void
    handleEquipmentSearch: (search: string) => void
  }
  loading: {isFetchingAreas: boolean; isFetchingLines: boolean; isFetchingEquipments: boolean}
  selectedNames: {selectedProductionArea: string; selectedParentForEquipment: string}
  paramKeys: {PRODUCTION_AREA: string; LINE: string; EQUIPMENT: string}
  updateParam: (key: string, value: string) => void
  onClear: () => void
}

// The funnel icon to the left of the "only show results for line|equipment|
// time_window" chips (AB#6170). Opens a small panel with the same
// area -> line -> equipment cascade used elsewhere in the app, applying each
// selection immediately (no separate confirm step) to narrow the Lines /
// Equipment / Time-window result sections.
const AppbarSearchHierarchyFilter: FC<Props> = ({
  filterState,
  activeCount,
  productionAreas,
  lines,
  equipments,
  searchHandlers,
  loading,
  selectedNames,
  paramKeys,
  updateParam,
  onClear,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [t] = useCustomTranslation('common')

  const handleOpen = (event: ReactMouseEvent<HTMLElement>): void => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = (): void => {
    setAnchorEl(null)
  }

  return (
    <>
      <Tooltip title={t('appbar_search_categories.hierarchy_filter_tooltip')} arrow>
        <IconButton
          size="small"
          onClick={handleOpen}
          data-testid="mbai-appbar-search-hierarchy-filter-button"
          data-cy="mbai-appbar-search-hierarchy-filter-button"
          aria-label={String(t('appbar_search_categories.hierarchy_filter_tooltip'))}
        >
          {/* Badge wraps the icon itself, not the IconButton -- wrapping the
              button anchors the badge to its padded hit-box instead of the
              visible triangle, so it drifts up-left and overlaps whatever
              sits above the button (AB#6170 visual bug). */}
          <Badge
            badgeContent={activeCount}
            invisible={!activeCount}
            color="primary"
            data-cy="mbai-appbar-search-hierarchy-filter-badge"
          >
            <FilterAltIcon fontSize="small" />
          </Badge>
        </IconButton>
      </Tooltip>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
        data-testid="mbai-appbar-search-hierarchy-filter-popover"
      >
        <Box sx={{p: 2, minWidth: 220}} data-cy="mbai-appbar-search-hierarchy-filter-panel">
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 1,
              gap: 2,
            }}
          >
            <Typography variant="subtitle2" color="text.secondary">
              {t('filters')}
            </Typography>
            {activeCount > 0 ? (
              <Button
                size="small"
                onClick={onClear}
                data-cy="mbai-appbar-search-hierarchy-filter-clear"
              >
                {t('clear_all')}
              </Button>
            ) : null}
          </Box>
          <BasicHierarchyFiltersRow
            filterState={filterState}
            productionAreas={productionAreas}
            lines={lines}
            equipments={equipments}
            searchHandlers={searchHandlers}
            loading={loading}
            selectedNames={selectedNames}
            paramKeys={paramKeys}
            updateParam={updateParam}
            vertical
          />
        </Box>
      </Popover>
    </>
  )
}

export default AppbarSearchHierarchyFilter
